import React from 'react';
import { useTranslation } from 'react-i18next';
import DynamicForm from '../../../components/ui/dynamicForm/dynamicForm.component';
import { TENANT_SETTINGS_ENDPOINT } from '../../../consts';
import { GroupFields } from '../../../types/dynamicForm';
import {
  SETTINGS_API_FIELDS,
  SETTINGS_DYNAMIC_FIELDS,
  SETTINGS_SECTIONS,
  SettingsSectionKey,
  buildSettingsFieldAttributes,
  sectionEditFields,
} from '../tenantSettings.config';

interface EditTenantSettingsProps {
  section: SettingsSectionKey;
  onSuccess?: (data?: any) => void;
}

/**
 * Edits one section of the tenant settings. The settings endpoint patches, so
 * each section can be saved on its own instead of resubmitting every field the
 * workspace has.
 */
export const EditTenantSettings: React.FC<EditTenantSettingsProps> = ({ section, onSuccess }) => {
  const { t } = useTranslation();
  const { groups } = SETTINGS_SECTIONS[section];

  const handleSuccess = (data?: any) => {
    window.dispatchEvent(new Event('tenantSettingsUpdated'));
    onSuccess?.(data);
  };

  // A single-group section is already titled by the modal it opens in.
  const groupFields: GroupFields[] | undefined =
    groups.length > 1
      ? groups.map(({ title, editFields, copyFrom }) => ({
          title: t(title),
          fields: editFields,
          action: copyFrom && {
            label: t(copyFrom.label),
            disabled: (values) => !values[copyFrom.requiredField],
            disabledHint: t(copyFrom.missingHint),
            // The form holds the whole settings row, not only this section's
            // fields, so the source values are already loaded.
            getValues: (values) =>
              Object.fromEntries(
                Object.entries(copyFrom.fields).map(([target, source]) => [target, values[source] ?? null])
              ),
          },
        }))
      : undefined;

  return (
    <DynamicForm
      id="settings"
      url={TENANT_SETTINGS_ENDPOINT}
      onSuccess={handleSuccess}
      includeFields={sectionEditFields(section)}
      groupFields={groupFields}
      dynamicFields={SETTINGS_DYNAMIC_FIELDS}
      apiFields={SETTINGS_API_FIELDS}
      fieldAttributes={buildSettingsFieldAttributes(t)}
    />
  );
};
